// Reusable responsive AdSense in-page ad unit.
// Behaviour:
//   1. When the visitor is region-blocked (Africa exclusion on) and the admin
//      has enabled a Sponsor Card, render the sponsor card in place of the ad.
//   2. Otherwise render <ins class="adsbygoogle"> only when eligible and a
//      valid slot ID is configured. Fires an "ad_impression" analytics event
//      when the slot fills.
import React, { useEffect, useRef } from 'react';
import { track } from '@/lib/analytics';
import { useAdsEligibility, validSlot } from '@/lib/adsEligibility';
import SponsorCard from '@/components/SponsorCard';

export default function AdSlot({ slotKey, page, label = 'Advertisement', className = '' }) {
  const insRef = useRef(null);
  const pushed = useRef(false);
  const { settings, publisherId, enabled, regionBlocked, consent } = useAdsEligibility();

  const slot = settings?.ad_slots?.[slotKey] || '';
  const eligible = enabled && validSlot(slot);
  const sponsor = settings?.sponsor_card;
  const showSponsor = !!(regionBlocked && sponsor?.enabled && consent);

  useEffect(() => {
    if (!eligible || pushed.current || !insRef.current) return;
    try {
      window.adsbygoogle = window.adsbygoogle || [];
      window.adsbygoogle.push({});
      pushed.current = true;
    } catch { /* noop */ }
  }, [eligible]);

  useEffect(() => {
    if (!eligible || !insRef.current) return;
    const el = insRef.current;
    let reported = false;
    const report = () => {
      if (reported) return;
      reported = true;
      track('ad_impression', { slot: slotKey, page: page || '' });
    };
    const check = () => {
      const status = el.getAttribute('data-ad-status');
      if (status === 'filled') report();
    };
    check();
    const obs = new MutationObserver(check);
    obs.observe(el, { attributes: true, attributeFilter: ['data-ad-status'] });
    return () => obs.disconnect();
  }, [eligible, slotKey, page]);

  if (showSponsor) return <SponsorCard card={sponsor} className={className} compact={!label} />;
  if (!eligible) return null;

  return (
    <div data-testid={`ad-slot-${slotKey}`} className={`w-full ${label ? 'my-8' : 'my-0'} ${className}`}>
      {label && (
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-2 text-center">{label}</p>
      )}
      <ins
        ref={insRef}
        className="adsbygoogle"
        style={{ display: 'block', textAlign: 'center' }}
        data-ad-client={publisherId}
        data-ad-slot={slot}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </div>
  );
}
